/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { MED_DATE_ONLY_DISPLAY, formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import { Typography2, typography2 } from '@coursera/cds-core';

import RatingLine from 'bundles/reviews-common/components/cds/RatingLine';

import _t from 'i18n!nls/reviews';

const styles = {
  card: css`
    width: 100%;
    min-width: 0;
    flex: 1;
    min-height: 0;
    padding: var(--cds-spacing-300);
    background-color: var(--cds-color-neutral-background-primary);
    border: 1px solid var(--cds-color-grey-300);
    border-radius: var(--cds-border-radius-100);
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    box-sizing: border-box;
  `,
  courseNameTray: css`
    display: flex;
    flex-direction: row;
    align-items: top;
    max-width: 100%;
  `,
  authorName: css`
    margin: 0;
    font-weight: 700;
    color: var(--cds-color-neutral-primary);
    line-height: 1.25;
  `,
  ratingLine: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--cds-spacing-50);
    margin: 0 0 var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary);
  `,
  ratingWrapper: css`
    display: inline-flex;
    align-items: top;
    gap: var(--cds-spacing-50);
  `,
  ratingDate: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
  dotSeparator: css`
    color: var(--cds-color-neutral-primary-weak);
    margin: 0 var(--cds-spacing-50);
  `,
  reviewText: css`
    margin: 0 0 var(--cds-spacing-100);
    color: var(--cds-color-neutral-primary);
  `,
  courseName: css`
    margin-bottom: var(--cds-spacing-200);
    max-width: 100%;
    ${typography2.bodySecondary.font}
    ${typography2.bodySecondary.letterSpacing};

    > span {
      display: inline-block;
      width: 100%;
      text-wrap: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    span > span {
      font-weight: var(--cds-font-weight-600);
    }
  `,
};

export type TopReviewCardReview = {
  authorName: string;
  comment: string;
  rating: number;
  timestamp: string;
  courseName?: string;
};

type Props = {
  review: TopReviewCardReview;
};

const TopReviewCarouselCard = ({ review }: Props) => {
  const { comment, rating, timestamp, courseName } = review;

  const formattedDate = formatDateTimeDisplay(timestamp, MED_DATE_ONLY_DISPLAY);

  return (
    <article css={styles.card} data-testid="top-review-carousel-card">
      <div css={styles.ratingLine}>
        <RatingLine rating={rating} />
        <span css={styles.dotSeparator} aria-hidden="true">
          ·
        </span>
        <Typography2 component="span" variant="bodySecondary" css={styles.ratingDate}>
          <FormattedMessage message={_t('Reviewed {date}')} date={formattedDate} />
        </Typography2>
      </div>
      <div css={styles.courseNameTray}>
        {courseName && (
          <Typography2 component="span" variant="bodySecondary" css={styles.courseName}>
            <FormattedHTMLMessage message={_t('Course: {courseName}')} courseName={<span>{courseName}</span>} />
          </Typography2>
        )}
      </div>
      <Typography2 component="p" variant="bodySecondary" css={styles.reviewText}>
        {comment}
      </Typography2>
    </article>
  );
};

export default TopReviewCarouselCard;
